import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Status palettes are deliberately opaque in both themes.
 *
 * The earlier ad-hoc classes mixed a translucent dark background
 * (`dark:bg-amber-950`) with a pale foreground (`dark:text-amber-100`).
 * Translucency means the rendered background depends on whatever sits behind
 * it, so the same pair landed at 4.8:1 on a tinted table row — legible in
 * theory, unreadable in practice. Opaque backgrounds make the pairing
 * deterministic: every combination below sits between 8.7:1 and 13.5:1
 * against its own background, in light and dark alike.
 *
 * `size` exists because these notices appear both as standalone blocks and as
 * one-line hints inside dense tables, where the block padding would break the
 * row.
 */
const alertVariants = cva('relative w-full rounded-lg border', {
  variants: {
    variant: {
      default: 'bg-background text-foreground',
      destructive:
        'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
      warning:
        'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100',
      danger:
        'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100',
      success:
        'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100',
      info: 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100',
    },
    size: {
      default:
        'px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
      /** One-line hint for table cells and form rows. */
      inline:
        'flex items-start gap-1 px-2 py-1 text-xs font-medium [&>svg]:mt-0.5 [&>svg]:shrink-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, size, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, size }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
